import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { activeBeat } from "@/lib/timeline/book/BookLayout";
import {
  CAST_SITE_BOX_H_PX,
  castLocationStackBounds,
  mergeCastBounds,
  mergeCastPartRow,
} from "./metrics";
import { locationRunRelevantAtPlayhead } from "../../location/LocationVisibility/relevance";
import { onStageLayoutAdjustments } from "../../location/ActiveLocationGroup/layout";
import { activeLocationGroupsAtPlayhead } from "../../location/ActiveLocationGroup/build";
import { onStageRunIds } from "../../location/ActiveLocationGroup";
import { nearLocationPlacements } from "../../location/LocationLayout/near";
import { partLabelOffsetsForStack } from "../../location/LocationPartLabels/stackPartLabels";
import { allLocationRuns } from "../../location/LocationRun/catalog";
import { locationStackStagePosition } from "../../location/LocationMarker/stagePosition";
import {
  activeLocationRun,
  buildLocationRuns,
  locationPathOf,
  type LocationRun,
} from "../../location/LocationRun";

/** About one scene ahead, which is how far a landscape frame can see along the rail. */
const WAITING_ROW_AHEAD_MS = 10_000;
const RECENT_RUN_MS = 16_000;

export interface CastBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function includeActiveLocations(
  bounds: CastBounds | null,
  layout: BookTimelineLayout,
  viewportWidth: number,
  playheadMs: number,
): CastBounds | null {
  const sceneRuns = buildLocationRuns(layout.beats);
  const beat = activeBeat(layout, playheadMs);
  const activePath = beat === undefined ? [] : locationPathOf(beat);
  const runs = allLocationRuns(layout);
  const placements = nearLocationPlacements(layout, playheadMs, viewportWidth);
  const byRunId = new Map(placements.map((p) => [p.runId, p]));
  const runById = new Map(runs.map((run) => [run.runId, run]));
  const stageByRunId = new Map<string, { anchorX: number; topY: number }>();
  for (const run of runs) {
    // A whole book has thousands. Only those around the playhead can affect the frame.
    if (run.startMs - playheadMs > WAITING_ROW_AHEAD_MS || playheadMs - run.endMs > RECENT_RUN_MS) {
      continue;
    }
    const placement = byRunId.get(run.runId);
    if (placement === undefined) {
      continue;
    }
    stageByRunId.set(
      run.runId,
      locationStackStagePosition(
        layout, run, placement, playheadMs, viewportWidth,
      ),
    );
  }
  // After a fork, people still on the other rail keep their locations on stage.
  const groups = activeLocationGroupsAtPlayhead(layout, playheadMs);
  const adjust = onStageLayoutAdjustments(
    groups, stageByRunId, layout, playheadMs,
  );
  const activeRun = activeLocationRun(sceneRuns, playheadMs);
  for (const group of groups) {
    for (const runId of onStageRunIds(group, layout, playheadMs)) {
      const run = runById.get(runId);
      const stage = stageByRunId.get(runId);
      if (run === undefined || stage === undefined) {
        continue;
      }
      const shift = adjust.get(runId);
      bounds = includeRun(
        bounds,
        layout,
        run,
        shift?.anchorX ?? stage.anchorX,
        stage.topY + (shift?.topYDelta ?? 0),
        playheadMs,
        activePath,
        activeRun?.runId,
      );
    }
  }
  return includeWaitingRows(bounds, layout, runs, stageByRunId, playheadMs);
}

/**
 * Leave room for the rows of boxes waiting just ahead on the main rail. Height only:
 * the frame should not pull back to fit the next scene sideways.
 */
function includeWaitingRows(
  bounds: CastBounds | null,
  layout: BookTimelineLayout,
  runs: readonly LocationRun[],
  stageByRunId: ReadonlyMap<string, { anchorX: number; topY: number }>,
  playheadMs: number,
): CastBounds | null {
  if (bounds === null) {
    return null;
  }
  let { minY, maxY } = bounds;
  for (const run of runs) {
    const ahead = run.startMs - playheadMs;
    if (ahead <= 0 || ahead > WAITING_ROW_AHEAD_MS || (run.lane ?? 0) !== 0) {
      continue;
    }
    const stage = stageByRunId.get(run.runId);
    if (stage === undefined || !locationRunRelevantAtPlayhead(layout, run, playheadMs)) {
      continue;
    }
    minY = Math.min(minY, stage.topY);
    maxY = Math.max(maxY, stage.topY + CAST_SITE_BOX_H_PX);
  }
  return { ...bounds, minY, maxY };
}

function includeRun(
  bounds: CastBounds | null,
  layout: BookTimelineLayout,
  stackRun: LocationRun,
  anchorX: number,
  topY: number,
  playheadMs: number,
  activePath: readonly string[],
  activeRunId: string | undefined,
): CastBounds | null {
  const parts = partLabelOffsetsForStack(
    stackRun,
    layout,
    playheadMs,
    activePath,
    activeRunId,
  );
  const stack = castLocationStackBounds(anchorX, topY, false, parts.length);
  bounds = mergeCastBounds(bounds, stack);
  for (const part of parts) {
    bounds = mergeCastPartRow(
      bounds,
      anchorX,
      part.dx,
      stack.minY,
      stack.maxY,
    );
  }
  return bounds;
}
